// The Take Off tab of the desk view: the rates block and Repaint Summary (and, on a maintenance
// option, its set-up and Overall Summary, #515), then the template's table, area by area.
import type { Fields } from '../lib/device-db';
import { tickSource } from '../lib/tick-rows';
import { saveFields, saveScope } from '../lib/options';
import { jobPaintActions, jobRowActions, taggedPaints } from '../lib/job-prices';
import { siteWash } from '../lib/site-wash';
import { addPaint, defaultStartYear, genericPaints, paintsOf, removePaint, SECOND_REPAINT_COATS_SETTING, setRate, type OptionScope } from '@higgins/m2q-pricing';
import { MaintenanceSection } from './maintenance-section';
import { MaintenanceSetupBox, OverallSummary } from './maintenance-setup';
import { PaintList } from './paint-list';
import { RatesPanel, RepaintSummary } from './rates-panel';
import { TAKE_OFF_TABLE_CLASS, TakeOffHeader } from './take-off-rows';
import { LeftOutSection, TakeOffSection } from './take-off-section';
import type { TakeOffViewProps } from './take-off-view';

const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/** The Take Off tab: the rates block and Repaint Summary, and the template's table. */
export function TakeOffSheet({ measure, records, option, reference, scope, labourType, rates, baseRates, sections, info, priced, maintenance, priceShown }: TakeOffViewProps & { priceShown: boolean }) {
  const contract = maintenance && 'contract' in maintenance ? maintenance : null;
  const contractById = new Map((contract?.contract.sections ?? []).map((s) => [s.id, s]));
  const areas = new Map(records.filter((r) => r.table === 'area').map((r) => [r.id, r]));
  const pricedById = new Map(priced.sections.map((s) => [s.id, s]));
  const inputById = new Map(sections.map((s) => [s.id, s]));
  // Returned, so a text box knows its save is still under way (it asks "Leave site?" until then).
  const editScope = (change: (s: OptionScope) => OptionScope) => saveScope(option, change);
  const generic = genericPaints(reference);
  const paints = paintsOf(scope, generic);
  const job = jobPaintActions(measure, records, (paint) => editScope((s) => addPaint(s, generic, paint)));
  const jobRows = jobRowActions(measure, records);
  let index = 0;

  // The rates sit beside the table only from 98rem (1568px) wide, where the 18rem side column, the
  // page's margins and a window scrollbar still leave the table its 74.5rem; narrower, they go above
  // it, side by side, so the table has the full width (#441).
  return (
    <div className="grid gap-3 min-[98rem]:grid-cols-[18rem_minmax(0,1fr)]">
      <div className="grid content-start items-start gap-3 md:grid-cols-2 min-[98rem]:grid-cols-1">
        <RatesPanel
          rates={rates}
          base={baseRates}
          changes={scope.rates}
          onRate={(key, half, value) => editScope((s) => setRate(s, key, half, value))}
          rateName={`${reference.branch.name ?? ''} · ${labourType}`}
          paintList={<PaintList paints={paints} rates={rates} date={reference.date} onAdd={(paint) => editScope((s) => addPaint(s, generic, paint))} onRemove={(key) => editScope((s) => removePaint(s, generic, key))} job={job} />}
        />
        <RepaintSummary option={priced} priceShown={priceShown} />
        {contract && (
          <>
            <MaintenanceSetupBox
              pricing={contract}
              choice={scope.maintenance}
              labourType={labourType}
              defaultCoats={reference.settings[SECOND_REPAINT_COATS_SETTING] ?? 0}
              defaultStartYear={defaultStartYear(reference)}
              onScope={editScope}
              onFields={(fields) => saveFields(option, fields)}
            />
            <OverallSummary pricing={contract} priceShown={priceShown} />
          </>
        )}
      </div>
      <div className="@container overflow-x-auto rounded-lg border border-slate-200 bg-white">
        {info.size === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">No areas measured yet.</p>
        ) : (
          <table className={TAKE_OFF_TABLE_CLASS}>
            <TakeOffHeader />
            {[...info.entries()].map(([areaId, facts]) => {
              if (facts.error) return null;
              index += 1;
              const record = areas.get(areaId);
              const edits = {
                areaId,
                optionId: option.id,
                onArea: (fields: Fields) => {
                  if (record) void saveFields(record, fields);
                },
                onScope: editScope,
                job: jobRows,
              };
              const sectionPriced = pricedById.get(areaId);
              const sectionInput = inputById.get(areaId);
              if (facts.excluded || !sectionPriced || !sectionInput) return <LeftOutSection key={areaId} index={index} info={facts} edits={edits} />;
              const serviced = contractById.get(areaId);
              return [
                <TakeOffSection
                  key={areaId}
                  index={index}
                  input={sectionInput}
                  priced={sectionPriced}
                  info={facts}
                  rates={rates}
                  area={{
                    downtime: num(record?.fields.hig_downtimepercent),
                    abseil: num(record?.fields.hig_abseilpercent),
                    siteReqs: tickSource(records, 'areatag', record),
                    wash: record ? siteWash(record.fields) : { wash: false, washOnly: false },
                  }}
                  choice={scope.areas?.[areaId]}
                  edits={edits}
                  paints={taggedPaints(paints, rates)}
                  genericPaints={generic}
                  hireItems={reference.hireItems}
                />,
                contract && serviced && (
                  <MaintenanceSection key={`${areaId}-maintenance`} areaId={areaId} side={facts.side} section={serviced} choice={scope.maintenance} startYear={contract.startYear} onScope={editScope} />
                ),
              ];
            })}
          </table>
        )}
        <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
          <span className="rounded bg-sky-50 px-1">blue</span> measured on site (iPad) — change it on the area&rsquo;s measuring lines ·{' '}
          <span className="rounded bg-amber-50 px-1">yellow</span> changed for this option (clear it to go back) · down time and abseil change the area itself, for every option. Price check: {priced.priceCheckOk ? 'OK' : 'does not balance'}.
        </p>
      </div>
    </div>
  );
}
